import { BannerUpsellColor } from '@freelancer/ui/banner-upsell';
import { Currency } from '../currencies/currencies.model';

export enum CloseableDashboardBannerId {
  CORPORATE_UPSELL = 'dashboard_corporate_upsell',
  EXAM_UPSELL = 'dashboard_exam_upsell',
  FREELANCER_VERIFIED = 'freelancer_verified',
  WELCOME_BACK = 'dashboard_welcome_back',
}

export enum BannerId {
  NONE = 'none',

  // dashboard
  CORPORATE_UPSELL = 'corporate_upsell',
  EXAM_UPSELL = 'exam_upsell',
  FREELANCER_VERIFIED = 'freelancer_verified',
  GIVE_GET = 'give_get',
  MEMBERSHIP_SALE = 'membership_sale',
  MISSING_FIRST_MILESTONE = 'missing_first_milestone',
  PROJECT_MANAGEMENT = 'project_management',
  QUOTATION_FREELANCER_UPSELL = 'quotation_freelancer_upsell',
  WELCOME_BACK = 'welcome_back',

  // profile widget upsell
  MEMBERSHIP_UPGRADE_UPSELL = 'membership_upgrade_upsell',
  GET_STARTED = 'get_started',

  // verification
  REFERRAL_BONUS = 'referral_bonus',
  PARTNERSHIP_PAYMENT_VERIFICATION = 'partnership_payment_verification',
  EMAIL_VERIFICATION = 'email_verification',
  PENDING_INFORMATION_REQUEST = 'pending_information_request',
  VAT_VERIFICATION = 'vat_verification',
  TCS_VERIFICATION = 'tcs_verification',
  TDS_PAN_REQUEST = 'tds_pan_request',
  TDS_PAN_REQUEST_FOR_AADHAAR = 'tds_pan_request_for_aadhaar',
  MXIT_RFC_REQUEST = 'mxit_rfc_request',
  CALIFORNIAN_VERIFICATION = 'californian_verification',
  CREDIT_CARD_VERIFICATION = 'credit_card_verification',
  CREDIT_CARD_VERIFICATION_IN_PROGRESS = 'credit_card_verification_in_progress',
  FORCE_PHONE_VERIFICATION = 'force_phone_verification',
  SECURE_PHONE_SETUP = 'secure_phone_setup',
  ACCOUNT_LIMITED = 'account_limited',
  KYC = 'kyc',
  CORPORATE_KYC = 'corporate_kyc',
  FORCE_CREDIT_CARD_VERIFICATION = 'force_credit_card_verification',
  EXPIRING_CREDIT_CARD = 'expiring_credit_card',
  PHONE_VERIFICATION = 'phone_verification',
  INDIA_MEMBERSHIP_RENEWAL_DISABLED = 'india_membership_renewal_disabled',
  INDIA_MEMBERSHIP_MANDATE_REQUIRED = 'india_membership_mandate_required',
  NEGATIVE_BALANCE_NOTIFICATION = 'negative_balance_notification',
  US_TAX_TIN_REQUEST = 'us_tax_tin_request',
}

export const enum BannerType {
  DASHBOARD = 'dashboard',
  PROFILE_WIDGET_UPSELL = 'profile_widget_upsell',
  USER_PROFILE = 'user_profile',
  VERIFICATION = 'verification',
}

export interface BannerBase {
  readonly id: BannerId;
  readonly type: BannerType;
}

/**
 * A banner for various membership and give-get promotions.
 * Primarily used on the dashboard (https://www.freelancer.com/dashboard).
 */
export type Banner =
  | AccountLimitedBanner
  | CalifornianVerificationBanner
  | CorporateKycBanner
  | CorporateUpsellBanner
  | CreditCardVerificationBanner
  | CreditCardVerificationInProgressBanner
  | EmailVerificationBanner
  | ExamUpsellBanner
  | ExpiringCreditCardBanner
  | ForceCreditCardVerificationBanner
  | ForcePhoneVerificationBanner
  | FreelancerVerifiedBanner
  | GettingStartedBanner
  | GiveGetBanner
  | IndiaMembershipMandateRequiredBanner
  | IndiaMembershipRenewalDisabledBanner
  | KycBanner
  | MembershipUpgradeUpsellBanner
  | MembershipSaleBanner
  | MissingFirstMilestoneBanner
  | NoBanner
  | PartnershipPaymentVerificationBanner
  | PendingInformationRequestBanner
  | PhoneVerificationBanner
  | ProjectManagementBanner
  | ReferralBonusBanner
  | QuotationFreelancerUpsellBanner
  | SecurePhoneSetupBanner
  | TcsVerificationBanner
  | TdsPanRequestBanner
  | TdsPanRequestForAadhaarBanner
  | MexicanIncomeTaxRfcRequestBanner
  | VatVerificationBanner
  | WelcomeBackBanner
  | NegativeBalanceNotificationBanner
  | UsTaxTinRequestBanner;

/* Dashboard banners */
export interface MissingFirstMilestoneBanner extends BannerBase {
  readonly id: BannerId.MISSING_FIRST_MILESTONE;
  readonly projectTitle: string;
  readonly projectUrl: string;
  readonly sellerDisplayName: string;
}

export interface WelcomeBackBanner extends BannerBase {
  readonly id: BannerId.WELCOME_BACK;
  readonly reward: WelcomeBackReward;
}

export interface FreelancerVerifiedBanner extends BannerBase {
  readonly id: BannerId.FREELANCER_VERIFIED;
}

export interface ExamUpsellBanner extends BannerBase {
  readonly id: BannerId.EXAM_UPSELL;
}

export interface GiveGetBanner extends BannerBase {
  readonly id: BannerId.GIVE_GET;
}

export interface ProjectManagementBanner extends BannerBase {
  readonly id: BannerId.PROJECT_MANAGEMENT;
}

export interface NoBanner extends BannerBase {
  readonly id: BannerId.NONE;
}

export interface WelcomeBackReward {
  readonly amount: number;
  readonly currency: Currency;
}

/* Profile widget upsell banners */
export interface MembershipUpgradeUpsellBanner extends BannerBase {
  readonly id: BannerId.MEMBERSHIP_UPGRADE_UPSELL;
}

export interface GettingStartedBanner extends BannerBase {
  readonly id: BannerId.GET_STARTED;
}

export interface ReferralBonusBanner extends BannerBase {
  readonly id: BannerId.REFERRAL_BONUS;
  readonly currencyCode: string;
  readonly amount: number;
  readonly referrer: string;
}

export interface PartnershipPaymentVerificationBanner extends BannerBase {
  readonly id: BannerId.PARTNERSHIP_PAYMENT_VERIFICATION;
}

export interface EmailVerificationBanner extends BannerBase {
  readonly id: BannerId.EMAIL_VERIFICATION;
  readonly email: string;
  readonly name: string;
  readonly userId: number;
  readonly key: string;
}

export interface PendingInformationRequestBanner extends BannerBase {
  readonly id: BannerId.PENDING_INFORMATION_REQUEST;
  readonly daysLeft: number;
}

export interface VatVerificationBanner extends BannerBase {
  readonly id: BannerId.VAT_VERIFICATION;
}

export interface TcsVerificationBanner extends BannerBase {
  readonly id: BannerId.TCS_VERIFICATION;
}

export interface TdsPanRequestBanner extends BannerBase {
  readonly id: BannerId.TDS_PAN_REQUEST;
}

export interface TdsPanRequestForAadhaarBanner extends BannerBase {
  readonly id: BannerId.TDS_PAN_REQUEST_FOR_AADHAAR;
}

export interface MexicanIncomeTaxRfcRequestBanner extends BannerBase {
  readonly id: BannerId.MXIT_RFC_REQUEST;
}

export interface CreditCardVerificationBanner extends BannerBase {
  readonly id: BannerId.CREDIT_CARD_VERIFICATION;
}

export interface CreditCardVerificationInProgressBanner extends BannerBase {
  readonly id: BannerId.CREDIT_CARD_VERIFICATION_IN_PROGRESS;
}

export interface ForcePhoneVerificationBanner extends BannerBase {
  readonly id: BannerId.FORCE_PHONE_VERIFICATION;
}

export interface SecurePhoneSetupBanner extends BannerBase {
  readonly id: BannerId.SECURE_PHONE_SETUP;
}

export interface AccountLimitedBanner extends BannerBase {
  readonly id: BannerId.ACCOUNT_LIMITED;
}

export interface KycBanner extends BannerBase {
  readonly id: BannerId.KYC;
}

export interface CorporateKycBanner extends BannerBase {
  readonly id: BannerId.CORPORATE_KYC;
}

export interface ForceCreditCardVerificationBanner extends BannerBase {
  readonly id: BannerId.FORCE_CREDIT_CARD_VERIFICATION;
  readonly isForceCcImageVerification: boolean;
}

export interface ExpiringCreditCardBanner extends BannerBase {
  readonly id: BannerId.EXPIRING_CREDIT_CARD;
  readonly daysLeft: number;
}

export interface PhoneVerificationBanner extends BannerBase {
  readonly id: BannerId.PHONE_VERIFICATION;
  readonly name: string;
}

export interface CalifornianVerificationBanner extends BannerBase {
  readonly id: BannerId.CALIFORNIAN_VERIFICATION;
}

export interface CorporateUpsellBanner extends BannerBase {
  readonly id: BannerId.CORPORATE_UPSELL;
}

export interface MembershipSaleBanner extends BannerBase {
  readonly id: BannerId.MEMBERSHIP_SALE;
  readonly backgroundImageUrl: string;
  readonly bannerUpsellColor: BannerUpsellColor;
  readonly subtitle: string;
  readonly timeEnd: number;
  readonly title: string;
}

export interface IndiaMembershipRenewalDisabledBanner extends BannerBase {
  readonly id: BannerId.INDIA_MEMBERSHIP_RENEWAL_DISABLED;
  readonly packageName: string;
}

export interface IndiaMembershipMandateRequiredBanner extends BannerBase {
  readonly id: BannerId.INDIA_MEMBERSHIP_MANDATE_REQUIRED;
}

export interface NegativeBalanceNotificationBanner extends BannerBase {
  readonly id: BannerId.NEGATIVE_BALANCE_NOTIFICATION;
  readonly currencyId: number;
  readonly amountInMinorUnits: number;
}

export interface UsTaxTinRequestBanner extends BannerBase {
  readonly id: BannerId.US_TAX_TIN_REQUEST;
}

export interface QuotationFreelancerUpsellBanner extends BannerBase {
  readonly id: BannerId.QUOTATION_FREELANCER_UPSELL;
}
